import { describe, expect,it } from 'vitest';

import { CARD_CATEGORIES } from '../../../../../../../shared/cardConfig/constants';
import {
  ATTACHMENT_FIELDS,
  CONTACT_FIELDS,
  CONTACT_SAME_AS_FIELDS,
  DELIVERY_DETAILS_FIELDS,
  DELIVERY_INSTRUCTIONS_FIELDS,
  SHIP_TO_ADDRESS_FIELDS} from '../../../../../../../shared/fieldConfig/constants';
import { PAGE_ROUTES } from '../../../../../../../utils/constants';
import pages from '..';

// ----------------- Type Definitions -----------------
interface Field {
  key: string;
  required?: boolean;
}

interface Card {
  key: string;
  hideSameAs?: boolean;
  fields?: Field[];
  fieldWrapper?: string;
  sameAsFields?: string[];
}

// ----------------- Tests -----------------
describe('ShipTo Page Template', () => {
  it('exports one page', () => {
    expect(Array.isArray(pages)).toBe(true);
    expect(pages).toHaveLength(1);
  });

  const shipToPage = pages[0];

  it('has correct route', () => {
    expect(shipToPage.route).toBe(PAGE_ROUTES.shipTo);
  });

  it('contains expected cards', () => {
    expect(Array.isArray(shipToPage.cards)).toBe(true);
    expect(shipToPage.cards).toHaveLength(7);
  });

  // ------------------ Address Card ------------------
  describe('Address Card', () => {
    const addressCard = shipToPage.cards.find(c => c.key === CARD_CATEGORIES.address) as Card;

    it('has hideSameAs enabled', () => {
      expect(addressCard.hideSameAs).toBe(true);
    });

    it('has correct fields', () => {
      const keys = addressCard.fields?.map(f => f.key) ?? [];
      expect(keys).toEqual([SHIP_TO_ADDRESS_FIELDS.address, SHIP_TO_ADDRESS_FIELDS.taxNumber1]);
    });

    it('has correct fieldWrapper', () => {
      expect(addressCard.fieldWrapper).toBe(CARD_CATEGORIES.shipTo);
    });
  });

  // ------------------ Delivery Details Card ------------------
  describe('Delivery Details Card', () => {
    const deliveryDetailsCard = shipToPage.cards.find(c => c.key === CARD_CATEGORIES.deliveryDetails) as Card;

    it('contains expected delivery fields', () => {
      const keys = deliveryDetailsCard.fields?.map(f => f.key) ?? [];
      expect(keys).toEqual([
        DELIVERY_DETAILS_FIELDS.appointmentRequired,
        DELIVERY_DETAILS_FIELDS.receivingPlantContactName,
        DELIVERY_DETAILS_FIELDS.receivingPlantPhoneNumber,
        DELIVERY_DETAILS_FIELDS.afterHoursContactName,
        DELIVERY_DETAILS_FIELDS.afterHoursPhoneNumber,
        DELIVERY_DETAILS_FIELDS.warehouseReceivingHours
      ]);
    });
  });

  // ------------------ Delivery Instructions Card ------------------
  describe('Delivery Instructions Card', () => {
    const deliveryInstructionsCard = shipToPage.cards.find(c => c.key === CARD_CATEGORIES.deliveryInstructions) as Card;

    it('contains expected instruction fields', () => {
      const keys = deliveryInstructionsCard.fields?.map(f => f.key) ?? [];
      expect(keys).toEqual([
        DELIVERY_INSTRUCTIONS_FIELDS.shippingInstructions,
        DELIVERY_INSTRUCTIONS_FIELDS.shippingMarks,
        DELIVERY_INSTRUCTIONS_FIELDS.customsText,
        DELIVERY_INSTRUCTIONS_FIELDS.loadingInstructions,
        DELIVERY_INSTRUCTIONS_FIELDS.unloadingInstructions,
        DELIVERY_INSTRUCTIONS_FIELDS.packingInstructions,
        DELIVERY_INSTRUCTIONS_FIELDS.billOfLadingText,
        DELIVERY_INSTRUCTIONS_FIELDS.textNotaFiscal
      ]);
    });
  });

  // ------------------ COA Card ------------------
  describe('Cert of Analysis Card', () => {
    const coaCard = shipToPage.cards.find(c => c.key === CARD_CATEGORIES.coa) as Card;

    it('has correct fields', () => {
      const keys = coaCard.fields?.map(f => f.key) ?? [];
      expect(keys).toEqual([CONTACT_FIELDS.email]);
    });

    it('has correct fieldWrapper', () => {
      expect(coaCard.fieldWrapper).toBe(CARD_CATEGORIES.shipTo);
    });
  });

  // ------------------ Order Confirmation Card ------------------
  describe('Order Confirmation Card', () => {
    const orderCard = shipToPage.cards.find(c => c.key === CARD_CATEGORIES.orderConfirmation) as Card;

    it('has correct fields', () => {
      const keys = orderCard.fields?.map(f => f.key) ?? [];
      expect(keys).toEqual([CONTACT_FIELDS.email, CONTACT_FIELDS.fax]);
    });

    it('has sameAsFields set correctly', () => {
      expect(orderCard.sameAsFields).toEqual([CONTACT_SAME_AS_FIELDS.sameAsCoa]);
    });

    it('has correct fieldWrapper', () => {
      expect(orderCard.fieldWrapper).toBe(CARD_CATEGORIES.shipTo);
    });
  });

  // ------------------ Attachment & Privacy ------------------
  describe('Attachment & Privacy Cards', () => {
    it('contains additionalAttachmentCard', () => {
      const attachmentCard = shipToPage.cards.find(c => c.key === CARD_CATEGORIES.additionalAttachmentCard) as Card;
      expect(attachmentCard.fields?.[0].key).toBe(ATTACHMENT_FIELDS.additionalAttachment);
    });

    it('contains privacyPolicy card without fields', () => {
      const privacyCard = shipToPage.cards.find(c => c.key === CARD_CATEGORIES.privacyPolicy) as Card;
      expect(privacyCard.fields).toBeUndefined();
    });
  });
});